import React from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Button } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import GridColumns from '../../grid-columns';
import BrandDropdown from '../../brand-dropdown';
import BrandInfo from './brand-info';
import InputWithLabel from '../../input-with-header';
import Modal from '../../modal';
import BrandingsPropType from '../../../utils/prop-types/brandings';
import BrandPropType from '../../../utils/prop-types/brand';
import styles from './styles.css';

export const DEFAULT_BRAND = {
  id: 0,
  display_name: 'Not Set',
  image_url: 'https://images.gasbuddy.io/b/0.png',
};

export default function BrandEditModal({ buttonClassName, brandings: brandingsProps, displayName: displayNameProps, allBrands, onSave }) {
  const [brandings, setBrandings] = React.useState(brandingsProps);
  const [displayName, setDisplayName] = React.useState(displayNameProps);
  const [fuelBrand, setFuelBrand] = React.useState(null);
  const [cstoreBrand, setCstoreBrand] = React.useState(null);

  React.useEffect(() => {
    setBrandings(brandingsProps);
  }, [brandingsProps]);

  React.useEffect(() => {
    setDisplayName(displayNameProps);
  }, [displayNameProps]);

  React.useEffect(() => {
    if (brandings.fuel_brand_id) {
      setFuelBrand(allBrands.find(ab => ab.id === brandings.fuel_brand_id));
    } else if (brandings.fuel_brand_id === 0) {
      setFuelBrand(DEFAULT_BRAND);
    }
    if (brandings.cstore_brand_id) {
      setCstoreBrand(allBrands.find(ab => ab.id === brandings.cstore_brand_id));
    } else if (brandings.cstore_brand_id === 0) {
      setCstoreBrand(DEFAULT_BRAND);
    }
  }, [brandings]);

  const changeBranding = (newBrandId, brandingType) => {
    const brand = allBrands.find(ab => ab.id === newBrandId);
    const newBrandings = {
      ...brandings,
      [`${brandingType}_brand_id`]: brand?.id || 0,
      [`${brandingType}_brand_logo_url`]: brand?.image_url || '',
    };
    setBrandings(newBrandings);
  };

  const handleOnFuelBrandChange = (e) => {
    changeBranding(Number(e.currentTarget.value), 'fuel');
  };

  const handleOnCstoreBrandChange = (e) => {
    changeBranding(Number(e.currentTarget.value), 'cstore');
  };

  const handleDisplayNameChange = (e) => {
    setDisplayName(e.currentTarget.value);
  };

  const handleOnModalClose = () => {
    setBrandings(brandingsProps);
    setDisplayName(displayNameProps);
  };

  const handleOnSave = () => {
    onSave({ brandings, displayName });
  };

  return (
    <Modal
      title="EDIT BRANDS"
      size="md"
      saveButtonText="Save Changes"
      onSave={handleOnSave}
      onClose={handleOnModalClose}
      button={(
        <Button
          type="button"
          className={buttonClassName}
          link
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
      )}
    >
      <GridColumns
        contents={[
          {
            key: 'fuelBrand',
            title: undefined,
            body: (
              <FlexGrid>
                <FlexGrid.Column mobile={12} tablet={6}>
                  <div className={styles.brandWrapper}>
                    <BrandInfo isFuelBrand brand={fuelBrand} />
                  </div>
                </FlexGrid.Column>
                <FlexGrid.Column className={styles.brandDropdown} mobile={12} tablet={6}>
                  <BrandDropdown
                    className={styles.brandDropdown}
                    brands={allBrands}
                    selectedBrand={fuelBrand ? fuelBrand.id : 0}
                    onChange={handleOnFuelBrandChange}
                  />
                </FlexGrid.Column>
              </FlexGrid>
            ),
          },
          {
            key: 'cstoreBrand',
            title: undefined,
            body: (
              <FlexGrid>
                <FlexGrid.Column mobile={12} tablet={6}>
                  <div className={`${styles.brandWrapper} ${styles.secondBrandPadding}`}>
                    <BrandInfo isCstoreBrand brand={cstoreBrand} />
                  </div>
                </FlexGrid.Column>
                <FlexGrid.Column className={styles.brandDropdown} mobile={12} tablet={6}>
                  <BrandDropdown
                    brands={allBrands}
                    selectedBrand={cstoreBrand ? cstoreBrand.id : 0}
                    onChange={handleOnCstoreBrandChange}
                  />
                </FlexGrid.Column>
              </FlexGrid>
            ),
          },
        ]}
      />
      <InputWithLabel
        label="Public Display Name"
        className={styles.displayNameInput}
        value={displayName}
        editable
        onChange={handleDisplayNameChange}
      />
    </Modal>
  );
}

BrandEditModal.propTypes = {
  buttonClassName: PropTypes.string,
  brandings: BrandingsPropType.isRequired,
  displayName: PropTypes.string.isRequired,
  allBrands: PropTypes.arrayOf(BrandPropType).isRequired,
  onSave: PropTypes.func.isRequired,
};

BrandEditModal.defaultProps = {
  buttonClassName: '',
};
